/**
 * Servicio centralizado para manejar errores de Web Service
 * Este servicio se encarga de mostrar notificaciones al usuario cuando hay errores
 * de conexión o del servidor en las peticiones HTTP
 */

/**
 * Clase para manejar errores globales de Web Service
 */
class ErrorHandler {
  constructor() {
    this.errorNotificationCallback = null;
    this.i18n = null;
  }

  /**
   * Inicializa el servicio con la instancia de i18n
   * @param {Object} i18nInstance - Instancia de i18n de Vue
   */
  setI18n(i18nInstance) {
    this.i18n = i18nInstance;
  }

  /**
   * Obtiene un mensaje traducido
   * @param {string} key - Clave de traducción
   * @param {string} fallback - Mensaje por defecto
   */
  t(key, fallback) {
    if (this.i18n && this.i18n.global && this.i18n.global.t) {
      return this.i18n.global.t(key) || fallback;
    }
    return fallback;
  }

  /**
   * Registra el callback que se ejecutará cuando haya un error
   * @param {Function} callback - Función que recibe { type, message, status }
   */
  setNotificationCallback(callback) {
    this.errorNotificationCallback = callback;
  }

  /**
   * Extrae los detalles técnicos del error de axios para que el toast pueda
   * mostrarlos (código HTTP, body de la respuesta, payload enviado y
   * endpoint). El toast los pinta dentro de un <details> colapsable; los
   * mensajes genéricos por sí solos no daban información útil para
   * diagnosticar fallos contra el SOAP/CNXT.
   * @param {*} error - Error de axios (puede ser cualquier cosa)
   * @returns {{ code?: number|string, response?: unknown, payload?: unknown, endpoint?: string }}
   */
  extractDetails(error) {
    const details = {};
    const cfg = error?.config;
    if (cfg) {
      const method = (cfg.method || 'GET').toUpperCase();
      const url = cfg.url || '';
      if (url || method) {
        details.endpoint = `${method} ${url}`.trim();
      }
      if (cfg.data !== undefined) {
        details.payload = cfg.data;
      }
    }
    if (error?.response) {
      details.code = error.response.status;
      if (error.response.data !== undefined) {
        details.response = error.response.data;
      }
    } else if (error?.code) {
      // Errores de red / timeout no tienen response pero sí un código tipo
      // ERR_NETWORK / ECONNABORTED — útil para distinguir el tipo de fallo.
      details.code = error.code;
      if (error.message) details.response = error.message;
    }
    return details;
  }

  /**
   * Maneja errores de conexión con el servidor
   * @param {Error} error - Error de axios
   */
  handleConnectionError(error) {
    const message = this.t('error_conexion_servicio', 'No hay conexión con el servidor');


    this.notify({
      type: 'error',
      title: 'Error de Conexión',
      message: message,
      duration: 5000,
      ...this.extractDetails(error),
    });
  }

  /**
   * Maneja errores del servidor (5xx)
   * @param {Error} error - Error de axios con response
   * @param {number} status - Código de estado HTTP
   */
  handleServerError(error, status) {
    let message = this.t('error_desconocido', 'Error desconocido');

    // Mensajes específicos según el código de error
    if (status === 500) {
      message = 'Error interno del servidor (500)';
    } else if (status === 502) {
      message = 'Error de puerta de enlace (502)';
    } else if (status === 503) {
      message = 'Servicio no disponible (503)';
    } else if (status === 504) {
      message = 'Tiempo de espera agotado (504)';
    } else if (status >= 500) {
      message = `Error del servidor (${status})`;
    }


    this.notify({
      type: 'error',
      title: 'Error del Servidor',
      message: message,
      duration: 5000,
      status: status,
      ...this.extractDetails(error),
    });
  }

  /**
   * Maneja errores de cliente (4xx) excepto autenticación
   * @param {Error} error - Error de axios con response
   * @param {number} status - Código de estado HTTP
   */
  handleClientError(error, status) {
    // No manejar 401/403 aquí, ya se gestionan en el interceptor de autenticación
    if (status === 401 || status === 403) {
      return;
    }

    let message = `Error en la petición (${status})`;

    if (status === 404) {
      message = 'Recurso no encontrado (404)';
    } else if (status === 400) {
      message = 'Petición incorrecta (400)';
    } else if (status === 429) {
      message = 'Demasiadas peticiones (429)';
    }


    this.notify({
      type: 'warning',
      title: 'Error en la Petición',
      message: message,
      duration: 4000,
      ...this.extractDetails(error),
    });
  }

  /**
   * Muestra una notificación al usuario
   * @param {Object} notification - Configuración de la notificación
   */
  notify(notification) {
    if (this.errorNotificationCallback) {
      this.errorNotificationCallback(notification);
    } else {
      // Fallback: mostrar en consola si no hay callback registrado
    }
  }

  /**
   * Método para probar el sistema de notificaciones
   */
  testNotification() {
    this.notify({
      type: 'info',
      title: 'Test',
      message: 'Sistema de notificaciones funcionando correctamente',
      duration: 3000
    });
  }
}

// Exportar instancia única (singleton)
export default new ErrorHandler();
